import React from "react";
import { Search } from "lucide-react";

export interface AppointmentReportItem {
  id: string;
  date: string; // formatted date string
  time: string;
  dateObj?: Date;
  customerName: string;
  serviceName: string;
  staffName: string;
  duration: string;
  status: "Scheduled" | "Completed" | "Cancelled" | "No Show";
  price: string;
}

interface AppointmentsTableProps {
  appointments: AppointmentReportItem[];
  isLoading: boolean;
  indexOfFirstItem: number;
}

export const AppointmentsTable: React.FC<AppointmentsTableProps> = ({
  appointments,
  isLoading,
  indexOfFirstItem,
}) => {
  return (
    <div className="flex-1 lg:overflow-auto relative">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left font-bold whitespace-nowrap">#</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Date & Time</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Customer</th>
            <th className="col-span-3 text-left font-bold whitespace-nowrap">Service</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Staff</th>
            <th className="col-span-1 text-center font-bold whitespace-nowrap">Status</th>
            <th className="col-span-1 text-right font-bold whitespace-nowrap">Price</th>
          </tr>
        </thead>
        <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0 p-4 lg:p-0">
          {isLoading ? (
            <tr className="h-64 w-full flex items-center justify-center lg:table-row">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : appointments.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full lg:table-row">
              <td colSpan={12} className="w-full h-full p-0">
                <div className="flex flex-col items-center justify-center h-full w-full">
                  <Search className="w-12 h-12 mb-3 opacity-20" />
                  <p className="text-lg font-medium text-gray-500">
                    No appointments found
                  </p>
                  <p className="text-sm">Try adjusting your search terms</p>
                </div>
              </td>
            </tr>
          ) : (
            <>
              {appointments.map((apt, idx) => (
                <tr
                  key={apt.id}
                  className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-4 items-stretch lg:items-center px-5 py-4 lg:px-6 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full"
                >
                  <td className="lg:col-span-1 text-gray-400 font-bold lg:font-medium flex justify-between items-center lg:block">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Record #</span>
                    <span>{indexOfFirstItem + idx + 1}</span>
                  </td>

                  <td className="lg:col-span-2 flex flex-col lg:flex-row justify-between lg:justify-start items-stretch lg:items-center gap-1 lg:gap-0 mt-2 lg:mt-0 border-t lg:border-0 border-gray-50 pt-2 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold block">Date & Time</span>
                    <div className="flex flex-col">
                      <span className="font-bold lg:font-medium text-gray-900 lg:text-gray-800">{apt.date}</span>
                      <span className="text-xs text-gray-500 font-medium">{apt.time} • {apt.duration}</span>
                    </div>
                  </td>

                  <td className="lg:col-span-2 font-bold lg:font-medium text-gray-900 flex justify-between items-center lg:block border-t lg:border-0 border-gray-50 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Customer</span>
                    <span className="truncate">{apt.customerName}</span>
                  </td>

                  <td className="lg:col-span-3 text-gray-600 lg:text-gray-700 flex justify-between items-center lg:block border-t lg:border-0 border-gray-50 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Service</span>
                    <span className="truncate font-medium lg:font-normal">{apt.serviceName}</span>
                  </td>

                  <td className="lg:col-span-2 text-gray-600 flex justify-between items-center lg:block border-t lg:border-0 border-gray-50 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Staff</span>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-[10px] font-bold text-[#885ABB] flex-shrink-0">
                        {apt.staffName.charAt(0)}
                      </div>
                      <span className="truncate font-semibold lg:font-normal">{apt.staffName}</span>
                    </div>
                  </td>

                  <td className="lg:col-span-1 text-center flex justify-between items-center lg:block border-t lg:border-0 border-gray-50 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Status</span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 lg:py-1 rounded-md text-[10px] lg:text-xs font-bold lg:font-medium whitespace-nowrap ${apt.status === "Completed"
                          ? "bg-green-50 text-green-700 border border-green-100"
                          : apt.status === "Cancelled"
                            ? "bg-red-50 text-red-700 border border-red-100"
                            : apt.status === "No Show"
                              ? "bg-gray-100 text-gray-700 border border-gray-200"
                              : "bg-blue-50 text-blue-700 border border-blue-100"
                        }`}
                    >
                      {apt.status}
                    </span>
                  </td>

                  <td className="lg:col-span-1 text-right font-extrabold lg:font-bold text-[#885ABB] lg:text-gray-900 flex justify-between items-center lg:block border-t lg:border-0 border-gray-50 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Price</span>
                    <span className="text-base lg:text-sm">{apt.price}</span>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
